/**
 * useDriverPositions — 30-second poll for driver GPS positions.
 *
 * Phase 9 / Plan 09-05 — polls GET /v1/drivers/positions and provides the data
 * to DriverMap for rendering live + stale driver pins (DISPATCH-02).
 *
 * Poll cadence: 30 seconds (refetchInterval: 30_000).
 * staleTime: 25_000 so data is treated as fresh for 25 seconds (avoids unnecessary
 * re-renders on rapid focus/blur cycles).
 * refetchIntervalInBackground: false — pauses when the browser tab is hidden to
 * reduce unnecessary network traffic.
 *
 * Spec: PATTERNS.md use-driver-positions.ts; RESEARCH Pattern 6; DISPATCH-02.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { USER_PROFILE_QUERY_KEY } from '../../lib/use-user-timezone';
import type { DriverPosition } from '@lodex-pro/shared';

/** TanStack Query cache key for the driver positions endpoint. */
export const DRIVER_POSITIONS_QUERY_KEY = ['drivers', 'positions'] as const;

/** Response envelope from GET /v1/drivers/positions. */
interface DriverPositionsResponse {
  readonly positions: readonly DriverPosition[];
}

/**
 * Polls GET /v1/drivers/positions every 30 seconds.
 *
 * Returns standard TanStack Query result: `{ data, isLoading, isFetching, isError }`.
 * Use `isFetching` (not `isLoading`) for the "Refreshing..." text indicator after
 * the initial load (UI-SPEC §4 poll cadence note).
 *
 * @param enabled  When false, the query is disabled — no initial fetch and no 30s
 *                 poll. Quick 260619-map (#2): the map is hidden (display:none) when
 *                 the Map View tab is inactive, so polling pauses to avoid needless
 *                 network traffic while no one is looking at the map.
 */
export function useDriverPositions(
  enabled = true,
): ReturnType<typeof useApiQuery<DriverPositionsResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const query = useApiQuery<DriverPositionsResponse>(opts, '/v1/drivers/positions', {
    queryKey: DRIVER_POSITIONS_QUERY_KEY,
    enabled,
    // Pause the 30s poll entirely when inactive (false disables the interval).
    refetchInterval: enabled ? 30_000 : false,
    staleTime: 25_000,
    refetchIntervalInBackground: false,
  });
  // §58 (#877 review): LIVE_MAP was turned off while the map is open. Refetch the profile at
  // once, so the gated message replaces the map instead of a 403 every 30 s until it refreshes.
  const featureOff = query.error instanceof ApiError && query.error.code === 'FEATURE_NOT_ENABLED';
  React.useEffect(() => {
    if (featureOff) void qc.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
  }, [featureOff, qc]);
  return query;
}
